import { Box, Typography } from '@vp/swan';
import { AvailableDatePicker, BoxedLoader, bugtracker } from '@99designs/design-services-common';
import { __ } from '@99designs/i18n';
import { usePage } from '@99designs/tracking';
import { useDesignLiveAvailableDatesQuery } from './appointments.generated';

interface DateSelectorProps {
    timezone: string;
    date: string | null;
    setDate: (date: string | null) => void;
    immutable: boolean | null;
    calendarId?: number;
}

export default function DateSelector({
    timezone,
    setDate,
    date,
    immutable,
    calendarId,
}: DateSelectorProps) {
    const { locale } = usePage();

    const { data, loading, error } = useDesignLiveAvailableDatesQuery({
        variables: {
            timezone,
            locale,
            calendarId,
        },
        onCompleted: (d) => {
            const dates = d.setField.dates;

            // Confirm pre-saved date is still available
            const defaultDateInAvailableDates = dates
                .map((d) => ({
                    date: d.date.split('T')[0],
                    isAvailable: d.isAvailable,
                }))
                .find((dd) => dd.date === date && dd.isAvailable);

            if (defaultDateInAvailableDates) {
                setDate(date);
                return;
            }

            // No pre-saved date or pre-saved date not available. Default to the first available date
            const firstAvailable = dates.find((d) => d.isAvailable);
            if (firstAvailable) {
                setDate(firstAvailable.date.split('T')[0]);
            }
        },
        onError: (e) => {
            bugtracker.notify(`Failed to get available design live dates: ${e.message}`);
        },
    });

    if (error) {
        return (
            <Box marginTop="5">
                <Typography fontSize="small" textColor="error">
                    {__(
                        'There was an error fetching available dates. Please refresh the page to try again.'
                    )}
                </Typography>
            </Box>
        );
    }

    if (loading) {
        return <BoxedLoader label={__('Loading available dates...')} />;
    }

    const dates = data?.setField.dates;
    const hasAvailableDate = !!dates && dates.some((d) => d.isAvailable);

    if (!hasAvailableDate) {
        if (calendarId) {
            return (
                <Typography fontSize="small" data-testid="designer-no-availability">
                    {__('This designer has no availability. Please choose another designer.', {
                        _context:
                            'Shown when the specifically chosen DesignLive designer has no available appointment dates',
                    })}
                </Typography>
            );
        }
        return (
            <Typography fontSize="small">
                {__(`There are no available dates at this time. Please select "Review over email" or try
                again later.`)}
            </Typography>
        );
    }

    const firstAvailableDate = dates.find((d) => d.isAvailable);
    // Available dates are returned with a Z when they are actually local. Removing the Z fixes the timezone shift issue
    const minDate = firstAvailableDate
        ? new Date(firstAvailableDate.date.replace('Z', ''))
        : new Date();

    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 14);

    return (
        <AvailableDatePicker
            resetKey={`${timezone}:${calendarId ?? ''}`}
            selectedDate={date ? new Date(date + 'T00:00:00') : null}
            minDate={minDate}
            maxDate={maxDate}
            disabled={!!immutable}
            onDateChange={(d) => setDate(d ? dateToISOString(d) : null)}
        />
    );
}

function dateToISOString(date: Date) {
    // Use local date components to avoid UTC timezone shift
    const yyyy = String(date.getFullYear()).padStart(4, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');

    return `${yyyy}-${mm}-${dd}`;
}
